import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import ListSectionTemplate from "../../common/ListSectionTemplate";
import { useProfile, isOwner } from "../../../context/ProfileContext";
import { changeRole, inviteMember, listMembers, removeMember } from "../../../api/studioAPI";
import { colors, surfaces, buttons, form } from "../../../styles/index";

const ROLE_HELP = { owner: "manages the team, sees every plan", creator: "builds games, sees their own plans" };

// The org's members. Everyone sees who's in it; owners invite (an emailed link), change roles and remove people.
export default function MembersPanel() {
  const { profile } = useProfile();
  const owner = isOwner(profile) && !profile?.org?.is_demo;
  const [members, setMembers] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("creator");
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState("");

  const load = useCallback(() => {
    listMembers().then((d) => setMembers(d.items)).catch((e) => toast.error(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, done) => {
    setBusy(true);
    try {
      const out = await fn();
      if (done) toast.success(done);
      load();
      return out;
    } catch (e) {
      toast.error(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const invite = async () => {
    const out = await act(() => inviteMember(email.trim(), role), `Invitation sent to ${email.trim()}`);
    if (out) { setEmail(""); setLastLink(out.link || ""); }
  };

  return (
    <ListSectionTemplate title="Members" loading={members === null}>
      {owner && (
        <form style={{ ...surfaces.card, textAlign: "left", marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}
          onSubmit={(e) => { e.preventDefault(); if (email.trim()) invite(); }}>
          <label style={{ flex: "2 1 240px" }}>
            <span style={form.label}>Invite by email</span>
            <input type="email" style={form.input} value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com" disabled={busy} />
          </label>
          <label style={{ flex: "1 1 140px" }}>
            <span style={form.label}>Role</span>
            <select style={form.input} value={role} onChange={(e) => setRole(e.target.value)} disabled={busy}>
              <option value="creator">Creator</option>
              <option value="owner">Owner</option>
            </select>
          </label>
          <button type="submit" style={buttons.primary} disabled={busy || !email.trim()}>Send invitation</button>
          <p style={{ flexBasis: "100%", margin: 0, fontSize: "0.85rem", color: colors.textMuted }}>
            The link works for 24 hours. A creator {ROLE_HELP.creator}; an owner {ROLE_HELP.owner}.
          </p>
          {lastLink && (
            <p style={{ flexBasis: "100%", margin: 0, fontSize: "0.85rem", wordBreak: "break-all" }}>
              Email isn&rsquo;t set up on this server - send them this link yourself: <code>{lastLink}</code>
            </p>
          )}
        </form>
      )}
      <div style={{ ...surfaces.card, textAlign: "left", padding: 0 }}>
        {(members || []).map((m) => {
          const me = m.username === profile?.email;
          return (
            <div key={m.username} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
              borderBottom: `1px solid ${colors.border}`, flexWrap: "wrap" }}>
              <span style={{ fontSize: "1.3rem" }}>{m.avatar || "👤"}</span>
              <div style={{ flex: "1 1 200px" }}>
                <strong>{m.username}</strong>{me && <span style={{ color: colors.textMuted }}> (you)</span>}
                <div style={{ fontSize: "0.85rem", color: colors.textMuted }}>
                  {m.last_login ? `Last signed in ${new Date(m.last_login).toLocaleDateString()}` : "Hasn't signed in yet"}
                </div>
              </div>
              {owner && !me ? (
                <>
                  <select aria-label={`Role of ${m.username}`} value={m.role} disabled={busy}
                    onChange={(e) => act(() => changeRole(m.username, e.target.value), "Role changed")}
                    style={{ ...form.input, width: "auto", minHeight: 36, padding: "6px 10px" }}>
                    <option value="creator">Creator</option>
                    <option value="owner">Owner</option>
                  </select>
                  <button type="button" style={{ ...buttons.secondary, minHeight: 36, padding: "6px 12px" }} disabled={busy}
                    onClick={() => {
                      // eslint-disable-next-line no-alert
                      if (window.confirm(`Remove ${m.username}? Their plans stay with the organization.`)) {
                        act(() => removeMember(m.username), "Removed");
                      }
                    }}>
                    Remove
                  </button>
                </>
              ) : (
                <span style={{ color: colors.textMuted, textTransform: "capitalize" }}>{m.role}</span>
              )}
            </div>
          );
        })}
      </div>
    </ListSectionTemplate>
  );
}
